import { Box, Container, Flex, Stack } from '@chakra-ui/react'
import { type CampaignEntity, type ComponentCampaignCurrentNft } from '@twlvxtwlv/cms'
import type { CurrentNftWithProduct } from '@twlvxtwlv/mapping'
import type { AuctionWithHighestBid } from '@twlvxtwlv/supabase'
import type { Product } from '@twlvxtwlv/tokenforge'
import { type ArtistWithProducts, getProductAvailability } from '@twlvxtwlv/tokenforge'
import type {
  CampaignDetailsSlugType,
  CampaignNavigationRecords,
  NotifyMeAttributes,
  RoadmapEntry,
} from '@twlvxtwlv/types'
import { extractFirstQueryParamValue } from '@twlvxtwlv/utils/src/routing'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { DetailsMenu } from './details-menu'
import { About } from '../shared/about'
import { CampaignHero } from '../shared/campaign-hero'
import { ProductDetails } from '../shared/product-details'
import { Card } from '../../../../shared/cards/card'
import { type SelectedNftWithProduct, type TitleLeftElementType } from '../campaign-details'
import { RoadmapList } from '../shared/roadmap-list'

interface CampaignDetailsDesktopProps {
  artistsWithProducts: ArtistWithProducts[]
  auctionsWithHighestBid: AuctionWithHighestBid[]
  campaignContainsAward: boolean
  campaignEntity: CampaignEntity
  campaignNavigationRecords: CampaignNavigationRecords
  currentNftsWithProducts: CurrentNftWithProduct[]
  onNotifyMeAttributesChanged: (attributes: NotifyMeAttributes | null) => void
  onOpenNotifyMeModal: () => void
  onSelectedNftWithProductChanged: (nft: SelectedNftWithProduct | null) => void
  onTrackLearnMoreButtonClicked: (
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) => void
  onTrackNavigation: (slug: CampaignDetailsSlugType, product: Product | undefined | null) => void
  onTrackPurchaseButtonClicked: (
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) => void
  roadmapEntries: RoadmapEntry[]
}

export function CampaignDetailsDesktop({
  artistsWithProducts,
  auctionsWithHighestBid,
  campaignContainsAward,
  campaignEntity,
  campaignNavigationRecords,
  currentNftsWithProducts,
  onNotifyMeAttributesChanged,
  onOpenNotifyMeModal,
  onTrackLearnMoreButtonClicked,
  onTrackNavigation,
  onTrackPurchaseButtonClicked,
  roadmapEntries,
  onSelectedNftWithProductChanged,
}: CampaignDetailsDesktopProps) {
  const router = useRouter()
  const { t } = useTranslation('campaign')
  const [selectedNftWithProduct, setSelectedNftWithProduct] = useState<
    (CurrentNftWithProduct & TitleLeftElementType) | null
  >(null)

  const [selectedNavItem, setSelectedNavItem] = useState<{
    slug: CampaignDetailsSlugType
    productId: string | null
  }>({
    slug: (extractFirstQueryParamValue(router.query.show) as CampaignDetailsSlugType) ?? 'about',
    productId: extractFirstQueryParamValue(router.query['product-id']) ?? null,
  })
  const [itemsWithAvailability, setItemsWithAvailability] = useState<RoadmapEntry[]>([])

  useEffect(() => {
    const campaignId = campaignEntity.attributes?.tokenForge?.campaignId
    if (campaignId) {
      let path = `/campaign/${campaignId}/?show=${selectedNavItem.slug}`
      if (selectedNavItem.slug === 'token') {
        path += `&product-id=${selectedNavItem.productId}`
      }
      router.replace(path, undefined, {
        shallow: true,
      })
    }

    if (selectedNavItem.slug === 'token' && selectedNavItem.productId) {
      const currentNftWithProduct = currentNftsWithProducts.find(
        (cnwp) => cnwp.product.code === selectedNavItem.productId
      )
      const key = `token-${currentNftWithProduct?.product.code}`
      const titleLeftElement = campaignNavigationRecords[key].titleLeftElement
      if (currentNftWithProduct) {
        const newNftWithProduct = {
          ...currentNftWithProduct,
          titleLeftElement,
        }
        setSelectedNftWithProduct(newNftWithProduct)
        onSelectedNftWithProductChanged(currentNftWithProduct) // calling the function passed as prop with the new state
        onTrackNavigation(selectedNavItem.slug, currentNftWithProduct.product)
      }
    } else {
      setSelectedNftWithProduct(null)
      onSelectedNftWithProductChanged(null) // calling the function passed as prop with the new state
      onTrackNavigation(selectedNavItem.slug, currentNftsWithProducts[0]?.product ?? null)
    }
  }, [campaignEntity, selectedNavItem])
  // Do not add the router dependency in the useEffect dependency array!

  useEffect(() => {
    if (!currentNftsWithProducts || !roadmapEntries) return
    const newItemsWithAvailability = roadmapEntries.map((item) => {
      const currentNftWithProduct = currentNftsWithProducts?.find((nftWithProduct) => {
        return nftWithProduct.currentNft?.nftId === item.nftId
      })

      if (!currentNftWithProduct || !currentNftWithProduct.product) {
        return { ...item, availability: null }
      }

      //TODO: check if this is still correct after moving from variants to defaultVariant
      const totalOnHand = currentNftWithProduct.product.defaultVariant?.onHand ?? 0

      const availability = getProductAvailability(currentNftWithProduct.product, totalOnHand)
      return { ...item, availability: availability.availability }
    })

    setItemsWithAvailability(newItemsWithAvailability)
  }, [currentNftsWithProducts, roadmapEntries])

  return (
    <Flex direction="row" w="full" gap={4}>
      <Stack w="460px" minW="460px" maxW="460px" justify="center" gap={4} spacing={0}>
        <Box>
          <Card backgroundColor={campaignNavigationRecords.artist.backgroundColor}>
            <CampaignHero
              artist={artistsWithProducts[0]?.artist ?? null}
              campaignEntity={campaignEntity}
              isAwardTeaser={campaignContainsAward}
              isLabelTeaser={true}
            />
          </Card>
        </Box>
        <Box flexGrow={1}>
          <DetailsMenu
            campaignNavigationRecords={campaignNavigationRecords}
            onChange={(slug, productId) => {
              setSelectedNavItem({ slug, productId })
            }}
            showFaqButton={
              campaignEntity.id === '3' ||
              campaignEntity.id === '10' ||
              campaignEntity.id === '25' ||
              campaignEntity.id === '26'
            } // IDs for Campaign DIE DREI ???
          />
        </Box>
      </Stack>

      <Stack flexGrow={1}>
        {!selectedNftWithProduct &&
        selectedNavItem.slug === 'about' &&
        campaignNavigationRecords.about ? (
          <Card
            title={t(campaignNavigationRecords.about.titleTranslationKey ?? '')}
            backgroundColor={campaignNavigationRecords.about.backgroundColor}
          >
            <Container mx="initial" maxW="container.xl" p={0}>
              <About campaignEntity={campaignEntity} />
            </Container>
          </Card>
        ) : null}

        {selectedNftWithProduct && selectedNftWithProduct.currentNft ? (
          <Card
            key={selectedNftWithProduct.currentNft.id}
            title={`${selectedNftWithProduct.titleLeftElement} ${selectedNftWithProduct.currentNft.title}`}
            backgroundColor="white"
          >
            <Container mx="initial" maxW="container.xl" p={0}>
              <ProductDetails
                auctionsWithHighestBid={auctionsWithHighestBid}
                campaignId={campaignEntity.attributes?.tokenForge?.campaignId ?? null}
                currentNftWithProduct={selectedNftWithProduct}
                onOpenNotifyMeModal={onOpenNotifyMeModal}
                onTrackLearnMoreButtonClicked={onTrackLearnMoreButtonClicked}
                onTrackPurchaseButtonClicked={onTrackPurchaseButtonClicked}
              />
            </Container>
          </Card>
        ) : null}

        {!selectedNftWithProduct &&
        selectedNavItem.slug === 'roadmap' &&
        campaignNavigationRecords.roadmap ? (
          <Card
            title={t(campaignNavigationRecords.roadmap.titleTranslationKey ?? '')}
            backgroundColor={campaignNavigationRecords.roadmap.backgroundColor}
          >
            <Container mx="initial" maxW="container.xl" p={0}>
              <RoadmapList
                auctionsWithHighestBid={auctionsWithHighestBid}
                items={itemsWithAvailability}
                onOpenNotifyMeModal={onOpenNotifyMeModal}
                onNotifyMeAttributesChanged={onNotifyMeAttributesChanged}
              />
            </Container>
          </Card>
        ) : null}
      </Stack>
    </Flex>
  )
}
